import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, ChevronRight, Star, ArrowRight, Handshake, Sun, CalendarDays, Crown, Users } from "lucide-react";
import { SUBCATEGORIES, SPORTS } from "@/constants/categories";
import { api, resolveUrl } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";

const Panel = ({ children, className = "", testid }) => (
  <div data-testid={testid} className={`bg-white/95 rounded-2xl shadow-[0_8px_30px_-12px_rgba(2,132,197,0.25)] ring-1 ring-slate-100 p-3.5 ${className}`}>{children}</div>
);

const PanelTitle = ({ icon: Icon, color, children, link, linkLabel = "Δείτε όλες" }) => (
  <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1 mb-2.5">
    <h3 className="flex items-center gap-2 font-display font-extrabold text-[17px] text-slate-900 whitespace-nowrap"><Icon size={22} className={color} />{children}</h3>
    {link && <Link to={link} className="text-[11px] font-bold text-primary inline-flex items-center gap-0.5 whitespace-nowrap hover:gap-1.5 transition-[gap]" data-testid="panel-title-link">{linkLabel} <ArrowRight size={13} /></Link>}
  </div>
);

const Row = ({ to, img, title, sub, testid, rounded = "rounded-lg" }) => (
  <Link to={to} data-testid={testid} className="group flex items-center gap-3 py-1.5 border-b last:border-0 border-slate-100">
    <img src={img} alt={title} className={`h-11 w-16 shrink-0 object-cover ${rounded}`} />
    <div className="min-w-0 flex-1">
      <div className="font-bold text-sm text-slate-900 truncate">{title}</div>
      {sub && <div className="text-xs text-slate-500 truncate">{sub}</div>}
    </div>
    <ChevronRight size={18} className="text-slate-400 group-hover:translate-x-0.5 transition-transform" />
  </Link>
);

const AGES = [
  { label: "0 – 5 ετών", img: "/cats/vrefonipiakoi_v3.jpg", bg: "bg-sky-50", to: "/age/0-5" },
  { label: "6 – 12 ετών", img: "/cats/kdap_v3.jpg", bg: "bg-amber-50", to: "/age/6-12" },
  { label: "13 – 17 ετών", img: "/cats/kentra_meletis_v2.jpg", bg: "bg-pink-50", to: "/age/13-17" },
  { label: "Ενήλικες", img: "/brand/home_enilikes_card.jpg", bg: "bg-emerald-50", to: "/age/enilikes" },
];

const DISCOVER = [
  { title: "Κολύμβηση", sub: "Δράση & ευεξία για κάθε ηλικία", img: SPORTS.kolymvisi?.image, to: "/s/paidia/athlitikoi?sport=kolymvisi" },
  { title: "Μουσική", sub: "Έκφραση & δημιουργία", img: SUBCATEGORIES.sxoles_mousikis?.image, to: "/s/paidia/sxoles_mousikis" },
  { title: "Ρομποτική", sub: "Γνώση μέσα από το παιχνίδι", img: SUBCATEGORIES.stem?.image, to: "/s/paidia/stem" },
];

const DiscoverCard = ({ d }) => (
  <Link to={d.to} data-testid="sidebar-discover-item" className="group block">
    <div className="relative rounded-xl overflow-hidden">
      <img src={d.img} alt={d.title} className="w-full h-28 object-cover group-hover:scale-105 transition-transform duration-500" />
      <span className="absolute right-2.5 bottom-2.5 h-9 w-9 grid place-items-center rounded-full bg-white text-primary shadow-md group-hover:translate-x-0.5 transition-transform"><ChevronRight size={20} strokeWidth={2.6} /></span>
    </div>
    <div className="mt-1.5 font-display font-extrabold text-[15px] text-[#0B2454]">{d.title}</div>
    <div className="text-[13px] text-slate-500 leading-snug">{d.sub}</div>
  </Link>
);

const MONTHS = ["ΙΑΝ", "ΦΕΒ", "ΜΑΡ", "ΑΠΡ", "ΜΑΪ", "ΙΟΥΝ", "ΙΟΥΛ", "ΑΥΓ", "ΣΕΠ", "ΟΚΤ", "ΝΟΕ", "ΔΕΚ"];

export const LeftSidebar = () => {
  const { tt } = useLang();
  const [latest, setLatest] = useState([]);
  const [sponsor, setSponsor] = useState(null);
  useEffect(() => {
    api.get("/sponsors").then((r) => setSponsor(r.data[0] || null)).catch(() => {});
    api.get("/businesses", { params: { sort: "newest", limit: 4 } }).then((r) => setLatest(r.data)).catch(() => {});
  }, []);
  return (
    <div className="space-y-3" data-testid="home-left-sidebar">
      {sponsor && (
        <Panel testid="sidebar-sponsor">
          <PanelTitle icon={Crown} color="text-amber-400 fill-amber-400">Επίσημος χορηγός</PanelTitle>
          <a href={sponsor.link || "#"} target="_blank" rel="noopener noreferrer" data-testid="sidebar-sponsor-link" className="group relative block rounded-xl overflow-hidden">
            <img src={resolveUrl(sponsor.logo)} alt={sponsor.name} className="w-full h-32 object-cover group-hover:scale-105 transition-transform duration-500" />
            <span className="absolute bottom-2 right-2 inline-flex items-center gap-1 px-3 h-8 rounded-full bg-white text-slate-900 text-xs font-bold shadow">Μάθε περισσότερα <ArrowRight size={13} /></span>
          </a>
        </Panel>
      )}
      <Panel testid="sidebar-near-me">
        <Link to="/map" className="group flex items-center gap-3" data-testid="sidebar-near-me-link">
          <MapPin size={38} className="text-primary shrink-0" fill="currentColor" stroke="white" />
          <div className="flex-1">
            <div className="font-display font-extrabold text-lg text-slate-900">Κοντά μου</div>
            <div className="text-sm text-slate-600 leading-snug">Βρες δραστηριότητες στην περιοχή σου</div>
          </div>
          <ChevronRight size={20} className="text-slate-500 group-hover:translate-x-0.5 transition-transform" />
        </Link>
      </Panel>
      <Panel testid="sidebar-age-groups">
        <PanelTitle icon={Users} color="text-orange-500">Ηλικιακές ομάδες</PanelTitle>
        <div className="space-y-1.5">
          {AGES.map((a) => (
            <Link key={a.label} to={a.to} data-testid={`sidebar-age-${a.label}`} className={`group flex items-center gap-3 rounded-xl ${a.bg} pr-3 overflow-hidden hover:brightness-95 transition-[filter]`}>
              <img src={a.img} alt={a.label} className="h-10 w-14 object-cover" />
              <span className="flex-1 font-bold text-sm text-slate-900">{a.label}</span>
              <ChevronRight size={18} className="text-slate-500 group-hover:translate-x-0.5 transition-transform" />
            </Link>
          ))}
        </div>
      </Panel>
      <Panel testid="sidebar-latest">
        <PanelTitle icon={Star} color="text-amber-400 fill-amber-400">Νέες προσθήκες</PanelTitle>
        {latest.map((b) => (
          <Row key={b.id} to={`/activity/${b.id}`} testid="sidebar-latest-item" img={b.photos?.[0] ? resolveUrl(b.photos[0]) : SUBCATEGORIES[b.subcategory]?.image}
            title={b.name} sub={SUBCATEGORIES[b.subcategory] ? tt(SUBCATEGORIES[b.subcategory]) : b.city} />
        ))}
      </Panel>
    </div>
  );
};

export const RightSidebar = () => {
  const [events, setEvents] = useState([]);
  useEffect(() => {
    api.get("/events-upcoming", { params: { limit: 30 } }).then((r) => setEvents(r.data)).catch(() => {});
  }, []);
  return (
    <div className="space-y-3" data-testid="home-right-sidebar">
      {events.length > 0 && (
        <Panel testid="sidebar-events">
          <PanelTitle icon={CalendarDays} color="text-primary">Επερχόμενες εκδηλώσεις</PanelTitle>
          <div className="max-h-[400px] overflow-y-auto pr-1 -mr-1" data-testid="sidebar-events-list">
          {events.map((e) => {
            const d = new Date(e.date);
            return (
              <Link key={e.id} to={`/activity/${e.business_id}`} state={{ tab: "events", eventId: e.id }} data-testid="sidebar-event-item" className="group flex items-center gap-3 py-1.5 border-b last:border-0 border-slate-100">
                <div className="w-10 text-center shrink-0">
                  <div className="font-display font-extrabold text-xl text-slate-900 leading-none">{String(d.getDate()).padStart(2, "0")}</div>
                  <div className="text-[11px] font-bold text-slate-500">{MONTHS[d.getMonth()]}</div>
                </div>
                {e.cover && <img src={resolveUrl(e.cover)} alt={e.title} className="h-16 w-20 rounded-lg object-cover shrink-0" />}
                <div className="min-w-0 flex-1">
                  <div className="font-bold text-[15px] text-slate-900 line-clamp-2 leading-tight">{e.title}</div>
                  {(e.city || e.location) && <div className="text-xs text-slate-500 flex items-center gap-1 mt-0.5"><MapPin size={11} />{e.city || e.location}</div>}
                </div>
                <ChevronRight size={18} className="text-slate-400 group-hover:translate-x-0.5 transition-transform" />
              </Link>
            );
          })}
          </div>
        </Panel>
      )}
      <Panel testid="sidebar-discover">
        <PanelTitle icon={Sun} color="text-amber-400" link="/c/paidia" linkLabel="Δες περισσότερα">Ανακάλυψε κάτι νέο</PanelTitle>
        <div className="space-y-3">{DISCOVER.map((d) => <DiscoverCard key={d.title} d={d} />)}</div>
      </Panel>
      <Panel testid="sidebar-partner">
        <div className="flex items-start gap-3">
          <span className="h-12 w-12 shrink-0 grid place-items-center rounded-xl bg-sky-50 text-primary"><Handshake size={26} /></span>
          <div>
            <div className="font-display font-extrabold text-lg text-slate-900">Γίνε συνεργάτης</div>
            <div className="text-sm text-slate-600 leading-snug">Πρόβαλε την επιχείρησή σου στο SkillCity.</div>
          </div>
        </div>
        <Link to="/partner" data-testid="sidebar-partner-btn" className="mt-3 flex items-center justify-center gap-2 h-10 rounded-full bg-primary text-white font-bold text-sm hover:brightness-110 transition-[filter]">Μάθε περισσότερα <ArrowRight size={16} /></Link>
      </Panel>
    </div>
  );
};
